// ═══════════════════════════════════════════════════════════════
// SCORE AUDIT MODAL
// Full transparency into a member's engagement score.
// Shows every contact log entry with its contribution to the
// score: date, type, direction, base points, decay %, and
// current decayed points. Entries that don't score (outbound,
// expired, zero-type) are shown with an explanation.
//
// Props:
//   member     — member object (for name display)
//   engagement — return value from calculateEngagement()
//   onClose    — callback to close the modal
//
// Path: src/components/features/ScoreAuditModal.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef } from "react";
import {
  X,
  ListOrdered,
  ArrowDownRight,
  ArrowUpLeft,
  Clock,
  FileText,
  Send,
  TrendingUp,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";

// ── Status configuration ──────────────────────────────────────
const STATUS_CONFIG = {
  scoring: {
    label: "Scoring",
    color: "#10b981",
    bg: "#10b98118",
    icon: TrendingUp,
    description: null,
  },
  outbound: {
    label: "Outbound",
    color: "#6b7280",
    bg: "#6b728014",
    icon: Send,
    description: "Manager effort — not member engagement",
  },
  expired: {
    label: "Expired",
    color: "#9ca3af",
    bg: "#9ca3af10",
    icon: Clock,
    description: "Older than decay window",
  },
  "zero-type": {
    label: "Record",
    color: "#9ca3af",
    bg: "#9ca3af10",
    icon: FileText,
    description: "Type carries 0 points",
  },
};

// ── Contact type display labels ───────────────────────────────
const TYPE_LABELS = {
  email: "Email",
  phone: "Phone",
  call: "Phone",
  text: "Text",
  coffee: "Coffee",
  lunch: "Lunch",
  meeting: "Meeting",
  donation: "Donation",
  gift: "Donation",
  volunteering: "Volunteering",
  event: "Event",
  note: "Note",
};

// ── Direction badge ───────────────────────────────────────────
const DirectionBadge = ({ direction, palette }) => {
  const isInbound = direction === "inbound";
  const Icon = isInbound ? ArrowDownRight : ArrowUpLeft;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 10,
        fontWeight: 600,
        color: isInbound ? "#10b981" : "#6b7280",
        background: isInbound ? "#10b98115" : "#6b728012",
        padding: "1px 6px",
        borderRadius: 4,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        fontFamily: FONT_FAMILY,
      }}
    >
      <Icon size={ICON_SIZES.xs} />
      {isInbound ? "In" : "Out"}
    </span>
  );
};

// ── Format date for display ───────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const now = new Date();
  if (year === now.getFullYear()) return month + " " + day;
  return month + " " + day + ", " + year;
};

// ── Calculate days ago ────────────────────────────────────────
const daysAgo = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "today";
  if (diff === 1) return "1 day ago";
  return diff + " days ago";
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export const ScoreAuditModal = ({ member, engagement, onClose }) => {
  const { palette } = useApp();
  const overlayRef = useRef(null);

  // ── Escape key close ────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // ── Click-outside close ─────────────────────────────────────
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const breakdown = engagement?.breakdown || [];
  const score = engagement?.score || 0;
  const daysSince = engagement?.daysSinceContact;

  // Sort by date, newest first
  const sorted = [...breakdown].sort((a, b) => {
    const da = new Date(a.date || 0);
    const db = new Date(b.date || 0);
    return db - da;
  });

  // Summary counts
  const scoringCount = sorted.filter((e) => e.status === "scoring").length;
  const outboundCount = sorted.filter((e) => e.status === "outbound").length;
  const expiredCount = sorted.filter((e) => e.status === "expired").length;
  const zeroCount = sorted.filter((e) => e.status === "zero-type").length;
  const totalPoints = sorted.reduce((s, e) => s + (e.decayedPoints || 0), 0);

  const memberName = (member?.firstName || "") + " " + (member?.lastName || "");

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        style={{
          background: palette.cardBg,
          border: "1px solid " + palette.cardBorder,
          borderRadius: 12,
          width: "min(680px, 94vw)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 20px",
            borderBottom: "1px solid " + palette.cardBorder,
            flexShrink: 0,
          }}
        >
          <ListOrdered
            size={ICON_SIZES.md}
            style={{ color: palette.accent1, flexShrink: 0 }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: FONT_SIZES.md,
                fontWeight: 700,
                color: palette.text1,
              }}
            >
              Score Audit — {memberName.trim()}
            </div>
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                marginTop: 2,
              }}
            >
              {sorted.length} total entries · {scoringCount} scoring ·{" "}
              {outboundCount > 0 ? outboundCount + " outbound · " : ""}
              {expiredCount > 0 ? expiredCount + " expired · " : ""}
              {zeroCount > 0 ? zeroCount + " records" : ""}
            </div>
          </div>

          {/* Score badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginRight: 8,
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "6px 14px",
                borderRadius: 8,
                background: palette.accent1 + "18",
                border: "1px solid " + palette.accent1 + "30",
              }}
            >
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: palette.accent1,
                  lineHeight: 1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {score}
              </div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: palette.text3,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginTop: 2,
                }}
              >
                Score
              </div>
            </div>

            {daysSince !== null && (
              <div
                style={{
                  textAlign: "center",
                  padding: "6px 14px",
                  borderRadius: 8,
                  background:
                    daysSince > 60
                      ? "#ef444418"
                      : daysSince > 30
                      ? "#f59e0b18"
                      : "#10b98118",
                  border:
                    "1px solid " +
                    (daysSince > 60
                      ? "#ef444430"
                      : daysSince > 30
                      ? "#f59e0b30"
                      : "#10b98130"),
                }}
              >
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color:
                      daysSince > 60
                        ? "#ef4444"
                        : daysSince > 30
                        ? "#f59e0b"
                        : "#10b981",
                    lineHeight: 1,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {daysSince}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: palette.text3,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginTop: 2,
                  }}
                >
                  Days
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              borderRadius: 6,
              display: "flex",
              color: palette.text3,
            }}
            title="Close (Esc)"
          >
            <X size={ICON_SIZES.md} />
          </button>
        </div>

        {/* ── Table ──────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 20px 16px",
          }}
        >
          {sorted.length === 0 ? (
            <div
              style={{
                padding: "40px 0",
                textAlign: "center",
                fontSize: FONT_SIZES.sm,
                color: palette.text3,
              }}
            >
              No contact log entries found for this member.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
              }}
            >
              <thead>
                <tr
                  style={{
                    position: "sticky",
                    top: 0,
                    background: palette.cardBg,
                    zIndex: 1,
                  }}
                >
                  {[
                    { label: "Date", width: "18%", align: "left" },
                    { label: "Type", width: "15%", align: "left" },
                    { label: "Dir", width: "8%", align: "center" },
                    { label: "Base", width: "10%", align: "right" },
                    { label: "Decay", width: "10%", align: "right" },
                    { label: "Points", width: "11%", align: "right" },
                    { label: "Status", width: "28%", align: "left" },
                  ].map((col) => (
                    <th
                      key={col.label}
                      style={{
                        width: col.width,
                        textAlign: col.align,
                        padding: "10px 6px 8px",
                        fontWeight: 700,
                        color: palette.text2,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontSize: 10,
                        borderBottom: "2px solid " + palette.cardBorder,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry, idx) => {
                  const statusCfg =
                    STATUS_CONFIG[entry.status] || STATUS_CONFIG["zero-type"];
                  const isScoring = entry.status === "scoring";
                  const StatusIcon = statusCfg.icon;

                  return (
                    <tr
                      key={idx}
                      style={{
                        background:
                          idx % 2 === 0 ? "transparent" : statusCfg.bg,
                        opacity: isScoring ? 1 : 0.7,
                        transition: "background 0.1s",
                      }}
                    >
                      {/* Date */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          color: palette.text1,
                          fontWeight: isScoring ? 600 : 400,
                        }}
                      >
                        <div>{formatDate(entry.date)}</div>
                        <div
                          style={{
                            fontSize: 10,
                            color: palette.text3,
                            marginTop: 1,
                          }}
                        >
                          {daysAgo(entry.date)}
                        </div>
                      </td>

                      {/* Type */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          color: palette.text1,
                          fontWeight: isScoring ? 600 : 400,
                          textTransform: "capitalize",
                        }}
                      >
                        {TYPE_LABELS[entry.type] || entry.type}
                      </td>

                      {/* Direction */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          textAlign: "center",
                        }}
                      >
                        <DirectionBadge
                          direction={entry.direction}
                          palette={palette}
                        />
                      </td>

                      {/* Base Points */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          textAlign: "right",
                          color: palette.text2,
                          fontWeight: 500,
                        }}
                      >
                        {entry.basePoints > 0 ? entry.basePoints : "—"}
                      </td>

                      {/* Decay % */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          textAlign: "right",
                          color: palette.text3,
                          fontWeight: 500,
                        }}
                      >
                        {entry.status === "scoring"
                          ? Math.round(entry.decayFactor * 100) + "%"
                          : entry.status === "expired"
                          ? "0%"
                          : "—"}
                      </td>

                      {/* Decayed Points */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                          textAlign: "right",
                          fontWeight: isScoring ? 700 : 400,
                          color: isScoring ? palette.accent1 : palette.text3,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        {isScoring ? "+" + entry.decayedPoints : "0"}
                      </td>

                      {/* Status */}
                      <td
                        style={{
                          padding: "8px 6px",
                          borderBottom:
                            "1px solid " + palette.cardBorder + "60",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: statusCfg.color,
                            background: statusCfg.bg,
                            padding: "2px 8px",
                            borderRadius: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          <StatusIcon size={ICON_SIZES.xs} />
                          {statusCfg.label}
                        </span>
                        {statusCfg.description && (
                          <div
                            style={{
                              fontSize: 10,
                              color: palette.text3,
                              marginTop: 2,
                            }}
                          >
                            {statusCfg.description}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* ── Totals row ──────────────────────────────── */}
              <tfoot>
                <tr
                  style={{
                    borderTop: "2px solid " + palette.cardBorder,
                    background: palette.accent1 + "0a",
                  }}
                >
                  <td
                    colSpan={5}
                    style={{
                      padding: "10px 6px",
                      fontWeight: 700,
                      color: palette.text1,
                      textAlign: "right",
                      textTransform: "uppercase",
                      fontSize: 10,
                      letterSpacing: "0.05em",
                    }}
                  >
                    Total (before cap)
                  </td>
                  <td
                    style={{
                      padding: "10px 6px",
                      textAlign: "right",
                      fontWeight: 800,
                      fontSize: FONT_SIZES.sm,
                      color: palette.accent1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {Math.round(totalPoints * 10) / 10}
                  </td>
                  <td
                    style={{
                      padding: "10px 6px",
                      fontSize: 10,
                      color: palette.text3,
                    }}
                  >
                    {totalPoints > score
                      ? "Capped at " + score
                      : "Final: " + score}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {/* ── Legend ──────────────────────────────────────── */}
          {sorted.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid " + palette.cardBorder + "60",
              }}
            >
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 10,
                      color: palette.text3,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        color: cfg.color,
                        fontWeight: 600,
                      }}
                    >
                      <Icon size={ICON_SIZES.xs} />
                      {cfg.label}
                    </span>
                    {cfg.description
                      ? "— " + cfg.description
                      : "— Earning points"}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
